import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { Alert } from 'src/components/Alert';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { ListItem, ListItemSkeleton } from 'src/components/ListItem';
import { SearchTextField } from 'src/components/SearchTextField';
import { Stack } from 'src/components/Stack';
import { Theme } from 'src/components/muiTheme/types';
import { useMediaQuery } from 'src/components/muiTheme/utils';
import { getUserDisplayName } from 'src/models/user/utils';

import { useAddUsersDialog } from './useAddUsersDialog';

import type { Appointment } from 'src/models/appointment/_types';

interface Props {
  open: boolean;
  appointment: Appointment;
  onClose: () => void;
  defaultShowWaitlist?: boolean;
  activeTab: 'attendees' | 'waitlist';
  handleTabClick: (tab: 'attendees' | 'waitlist') => void;
}

export const AddUsersDialog: React.FC<Props> = ({
  open,
  appointment,
  onClose,
  activeTab = 'attendees',
  handleTabClick,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'admin.platform.appointment.drawer.addUsersDialog',
  });
  const showWaitList = activeTab === 'waitlist';

  const {
    searchMode,
    setSearchMode,
    searchFetching,
    searchUsers,
    searchValue,
    setSearchValue,
    handleSearch,
    handleResultClick,
    userList,
    userFetching,
    errorCount,
    handleAddUsers,
    handleCancel,
    handleRemoveUser,
    saving,
  } = useAddUsersDialog(appointment, onClose, showWaitList);
  const mdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  return (
    <Dialog
      title={t('addToServiceSession', { serviceName: appointment.serviceName })}
      open={open}
      fullScreen={mdDown}
      onClose={onClose}
      actions={
        !searchMode &&
        userList?.length && [
          {
            label: t('cancel'),
            onClick: () => {
              handleCancel();
            },
          },
          {
            label: t('saveChanges'),
            variant: 'contained',
            color: 'primary',
            disabled: saving,
            onClick: () => {
              handleAddUsers();
            },
          },
        ]
      }
    >
      <Stack sx={{ pt: 2, pb: 3 }}>
        {appointment.service.waitlist && (
          <Stack spacing={1} direction="row" sx={{ mb: 3 }}>
            <Chip
              active={!showWaitList}
              variant="outlined"
              onClick={() => {
                handleTabClick('attendees');
              }}
              label={t('addToSession')}
            />
            <Chip
              active={showWaitList}
              variant="outlined"
              onClick={() => {
                handleTabClick('waitlist');
              }}
              label={t('addToWaitlist')}
            />
          </Stack>
        )}
        <SearchTextField
          value={searchValue}
          onCancel={() => {
            setSearchMode(false);
            setSearchValue('');
          }}
          onSearch={handleSearch}
          icon={icon({ name: 'user-plus', style: 'regular' })}
          placeholder={t('addPeople')}
          showClearButton={searchMode}
          sx={{ mb: 3 }}
        />
        {searchMode && (
          <Box>
            {searchFetching && (
              <ListItemSkeleton sx={{ px: 0 }} avatar primary />
            )}
            {!searchFetching &&
              searchUsers?.map((r) => (
                <ListItem
                  sx={{ px: 0 }}
                  key={r.id}
                  avatar={<Avatar src={r.profileImage} />}
                  primary={r.name}
                  onClick={() => {
                    handleResultClick(r);
                  }}
                />
              ))}
          </Box>
        )}
        {!searchMode && (
          <>
            {errorCount > 0 && !showWaitList && (
              <Alert severity="warning">
                {t('warningsCount', { count: errorCount })}
              </Alert>
            )}
            <Box sx={{ mt: 0 }}>
              {userList?.map((u) => (
                <ListItem
                  sx={{ px: 0 }}
                  key={u.id}
                  avatar={<Avatar src={u.profileImage} />}
                  primary={getUserDisplayName(u)}
                  secondary={u.warning}
                  rightAdornment={
                    <IconButton
                      onClick={() => {
                        handleRemoveUser(u);
                      }}
                    >
                      <Icon
                        color="action"
                        icon={icon({ name: 'minus-circle', style: 'regular' })}
                      />
                    </IconButton>
                  }
                />
              ))}
              {userFetching && (
                <ListItemSkeleton sx={{ px: 0 }} avatar primary />
              )}
            </Box>
          </>
        )}
      </Stack>
    </Dialog>
  );
};
